/**
 * Auth Guard Component
 *
 * Route protection component that ensures only authenticated users can access
 * protected routes. Handles authentication state checking, redirect to login,
 * and destination path storage for post-login redirect.
 *
 * @module features/auth/components/AuthGuard
 * @see SPEC-005.5-auth-login-ui Requirements 5.1-5.6
 */

import { ReactNode, useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore, selectHasHydrated } from '../store/authStore';
import { useTypedTranslation } from '../../../i18n';

// ============================================================================
// Constants
// ============================================================================

/** sessionStorage key for storing redirect destination */
const REDIRECT_STORAGE_KEY = 'linkode_auth_redirect';

// ============================================================================
// Component Props
// ============================================================================

/**
 * AuthGuard component props
 */
export interface AuthGuardProps {
  /** Child components to render when authenticated */
  children: ReactNode;
}

// ============================================================================
// Component
// ============================================================================

/**
 * AuthGuard Component
 *
 * Protects routes by checking authentication status and redirecting
 * unauthenticated users to the login page. Stores the intended destination
 * for post-login redirect.
 *
 * Features:
 * - Checks authentication status on mount
 * - Redirects unauthenticated users to /login
 * - Stores intended destination in sessionStorage
 * - Shows loading state while checking auth
 * - Composable with React Router routes
 *
 * @example
 * ```tsx
 * // In App.tsx routes
 * <Route
 *   path="/editor"
 *   element={
 *     <AuthGuard>
 *       <EditorPage />
 *     </AuthGuard>
 *   }
 * />
 * ```
 *
 * @param props - Component props
 * @returns Protected content or loading/redirect state
 *
 * @see Requirements 5.1-5.6
 * @see Property 7: AuthGuard Redirect with Destination Storage
 */
export function AuthGuard({ children }: AuthGuardProps): JSX.Element | null {
  const { t } = useTypedTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Get auth state and actions from store
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const checkAuth = useAuthStore((state) => state.checkAuth);

  // CRITICAL: Wait for Zustand persist hydration before checking auth
  const hasHydrated = useAuthStore(selectHasHydrated);

  // Local state for tracking auth check completion
  const [isChecking, setIsChecking] = useState(true);

  // ─────────────────────────────────────────────────────────────────────────
  // Auth Check Effect
  // ─────────────────────────────────────────────────────────────────────────

  // Track if auth check has been performed to prevent duplicate checks
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    // CRITICAL: Wait for hydration before checking auth
    // Without this, sessionToken will be null even if user is authenticated
    if (!hasHydrated) {
      return;
    }

    // Only check auth once to prevent infinite loops
    if (hasCheckedRef.current) {
      return;
    }
    hasCheckedRef.current = true;

    // Check auth status after hydration is complete
    checkAuth();
    setIsChecking(false);
  }, [checkAuth, hasHydrated]);

  // ─────────────────────────────────────────────────────────────────────────
  // Redirect Effect
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    // Don't redirect while still checking auth
    if (isChecking) {
      return;
    }

    // If not authenticated, store destination and redirect to login
    if (!isAuthenticated) {
      // Store intended destination for post-login redirect
      // Requirements 5.3: Store the intended destination URL before redirecting
      sessionStorage.setItem(REDIRECT_STORAGE_KEY, location.pathname + location.search);

      // Redirect to login page
      // Requirements 5.2: Redirect unauthenticated users to /login
      navigate('/login', {
        replace: true,
        state: { from: location.pathname },
      });
    }
  }, [isChecking, isAuthenticated, location.pathname, location.search, navigate]);

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  // Show loading state while checking auth or waiting for hydration
  // Requirements 5.5: Display a loading state while checking authentication status
  if (!hasHydrated || isChecking) {
    return (
      <div className="auth-guard-loading" role="status" aria-live="polite">
        <div className="spinner" aria-hidden="true" />
        <p>{t('auth.guard.checking')}</p>
      </div>
    );
  }

  // Don't render children if not authenticated (redirect will happen)
  if (!isAuthenticated) {
    return null;
  }

  // Render protected content
  // Requirements 5.6: Composable with React Router routes
  return <>{children}</>;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get stored redirect destination from sessionStorage
 *
 * @returns Stored destination path or null if not set
 */
export function getStoredRedirectDestination(): string | null {
  return sessionStorage.getItem(REDIRECT_STORAGE_KEY);
}

/**
 * Clear stored redirect destination from sessionStorage
 *
 * Call this after successful authentication and redirect
 */
export function clearStoredRedirectDestination(): void {
  sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
}

/**
 * Get and clear stored redirect destination
 *
 * Convenience function that retrieves the stored destination and clears it
 * in one operation. Returns default path if no destination was stored.
 *
 * @param defaultPath - Default path to return if no destination stored (default: '/dashboard')
 * @returns Stored destination or default path
 *
 * @see Requirements 5.4, 2.5
 */
export function consumeStoredRedirectDestination(defaultPath: string = '/dashboard'): string {
  const destination = getStoredRedirectDestination();
  clearStoredRedirectDestination();
  return destination || defaultPath;
}
